import { Vessel, PartInstance } from '../core/vessel/vessel'
import { CATALOG, partDef } from '../core/vessel/catalog'
import { shapeHalfHeight, shapeRadius } from '../core/vessel/extents'
import { vec3, Vec3 } from '../core/math/vec3'
import { QUAT_IDENTITY, Quat, fromAxisAngle } from '../core/math/quat'

function part(
  id: string, defId: string, position: Vec3, stage: number,
  parentId: string | null, rotation: Quat = QUAT_IDENTITY,
): PartInstance {
  const def = partDef(CATALOG, defId)
  return { id, defId, position, rotation, stage, parentId, propellant: def.tank?.propellantMass ?? 0 }
}

/**
 * Razzo predefinito a due stadi, dimensionato per arrivare davvero in orbita.
 *
 *   stadio 0: motore Falco + due serbatoi grandi + quattro alette
 *   stadio 1: disaccoppiatore, motore da Vuoto, serbatoio medio, capsula
 *
 * Circa 5500 m/s di delta-v contro i 3400 teorici dell'orbita bassa: il resto
 * se lo mangiano la gravita e l'atmosfera durante la salita. Il rapporto
 * spinta/peso al decollo e 1.34, come un lanciatore vero.
 *
 * Le quote non sono scritte a mano: le parti si impilano dal basso leggendo
 * le altezze dal catalogo, e un test sorveglia che non restino buchi.
 */
export function defaultRocket(): Vessel {
  // dal basso verso l'alto: [id, defId, stadio]
  const pila: Array<[string, string, number]> = [
    ['motore-1', 'engine-falco', 0],
    ['serbatoio-a', 'tank-large', 0],
    ['serbatoio-b', 'tank-large', 0],
    ['disaccoppiatore', 'decoupler-axial', 1],
    ['motore-2', 'engine-vuoto', 1],
    ['serbatoio-c', 'tank-medium', 1],
    ['capsula', 'capsule-mk1', 1],
  ]

  let y = 0
  const quote = pila.map(([id, defId, stage]) => {
    const h = shapeHalfHeight(partDef(CATALOG, defId).shape)
    y += h
    const centro = y
    y += h
    return { id, defId, stage, y: centro }
  })

  // il genitore di ogni pezzo e quello che gli sta sopra: la radice e la capsula
  const impilate = quote.map((q, i) =>
    part(q.id, q.defId, vec3(0, q.y, 0), q.stage, quote[i + 1]?.id ?? null))

  // quattro alette in croce sul serbatoio piu basso, con la pala radiale
  const serbatoio = quote.find((q) => q.id === 'serbatoio-a')!
  const defSerbatoio = partDef(CATALOG, 'tank-large')
  const defAletta = partDef(CATALOG, 'fin-fixed')
  // il centro dell'aletta sta a mezza apertura oltre il fianco del serbatoio,
  // cosi la radice della pala tocca la superficie senza compenetrarla
  const semiApertura = defAletta.shape.kind === 'box' ? defAletta.shape.z / 2 : 0
  const raggio = shapeRadius(defSerbatoio.shape) + semiApertura
  const quotaAlette = serbatoio.y - shapeHalfHeight(defSerbatoio.shape) + shapeHalfHeight(defAletta.shape)

  const alette = [0, 1, 2, 3].map((i) => {
    const theta = (i * Math.PI) / 2
    // la pala della parte si estende lungo il suo +Z: va portata sulla radiale
    const phi = Math.PI / 2 - theta
    return part(
      `aletta-${i}`, 'fin-fixed',
      vec3(Math.cos(theta) * raggio, quotaAlette, Math.sin(theta) * raggio),
      0, 'serbatoio-a',
      fromAxisAngle(vec3(0, 1, 0), phi),
    )
  })

  return { rootId: 'capsula', parts: [...impilate, ...alette] }
}

/** Ordine di impilamento dal basso, usato dai test di geometria. */
export const DEFAULT_ROCKET_STACK = [
  'motore-1', 'serbatoio-a', 'serbatoio-b', 'disaccoppiatore', 'motore-2', 'serbatoio-c', 'capsula',
]

/** Descrizione leggibile degli stadi, per la UI. */
export const DEFAULT_ROCKET_STAGES = [
  { stage: 0, label: 'Primo stadio — Falco' },
  { stage: 1, label: 'Separazione e secondo stadio — Vuoto' },
]
